import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Grid,
  MenuItem,
  Typography,
  IconButton,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { resetGapBeneficiary } from './reducer/gapBeneficiaryReducer';
import { useBackroute } from '../../../contexts/BackrouteContext';
import ExitModal from '../../Common/ExitModal';
import dayjs from 'dayjs';
import { getAge } from '../../../v2/Utils/AgeCalc/calculateAge';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';

import BenefitCard from '../../../v2/Benefits/Beneficiary/Components/BenefitCard';
import FirmDetails from '../../../v2/Benefits/Beneficiary/Components/FirmDetails';
import IconReusable from '../../../components/IconReusable';
import {
  TextField,
  CustomSelect,
  CustomCheckbox,
  CustomButton,
  DatePicker,
} from '../../../v2/MUI_custom_component';
import { toast } from 'react-toastify';
import { submitBeneficiaryNominee } from './APIs/action';

const AddGapNominee = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const insuranceData = location?.state?.insuranceData;
  const returnTransactionId = location?.state?.returnTransactionId;
  const { updateBackroute, resetBackroute } = useBackroute();
  const dispatch = useDispatch();

  const beneficiary_details = useSelector(
    (state) => state?.gapBeneficiaryReducer || {},
  );
  const { selectedEntity: company_esi_id } = useSelector(
    (state) => state.entityReducer,
  );

  const [showExitModal, setShowExitModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    updateBackroute({ customHandler: () => setShowExitModal(true) });
    return () => resetBackroute();
  }, []);

  const [nomineeForm, setNomineeForm] = useState({
    relation: '',
    full_name: '',
    gender: '',
    dob: '',
    age: '',
  });
  const [confirmed, setConfirmed] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleFieldChange = (field, value) => {
    if (field === 'dob') {
      const birthDate = dayjs(value);
      const age = birthDate.isValid() ? dayjs().diff(birthDate, 'year') : '';
      setNomineeForm((prev) => ({
        ...prev,
        dob: value,
        age: age !== '' ? String(age) : '',
      }));
    } else {
      setNomineeForm((prev) => ({ ...prev, [field]: value }));
    }

    if (errors[field]) {
      setErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!nomineeForm.relation) newErrors.relation = 'Relation is required';
    if (!nomineeForm.full_name.trim())
      newErrors.full_name = 'Full name is required';
    else if (!/^[a-zA-Z\s]+$/.test(nomineeForm.full_name.trim()))
      newErrors.full_name = 'Name can only contain English letters';
    if (!nomineeForm.gender) newErrors.gender = 'Gender is required';
    if (!nomineeForm.dob) newErrors.dob = 'Date of birth is required';
    if (!confirmed) newErrors.confirmed = 'Please confirm to proceed';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    const payload = {
      version: 'v2',
      id_type: 1,
      company_esi_id,
      insurance_id: insuranceData?.benefit_card?.name,
      session_id: returnTransactionId,
      firm_owner_name: beneficiary_details?.firm_owner_name,
      firm_type: beneficiary_details?.firm_type,
      beneficiary: beneficiary_details?.beneficiary,
      nominee: {
        full_name: nomineeForm.full_name,
        gender: nomineeForm.gender,
        dob: nomineeForm.dob,
        relation: nomineeForm.relation,
      },
    };

    const { data, message, error } = await submitBeneficiaryNominee(payload);
    setSubmitting(false);
    if (!error) {
      toast.success(message || 'Gap beneficiary added successfully');
      navigate('/beneficiary/detail', {
        state: {
          insurance_type: insuranceData?.benefit_card?.name,
          selectedCompanyEntity: company_esi_id,
        },
      });
    } else {
      toast.error(message);
    }
  };

  const isSubmitDisabled =
    !nomineeForm.relation ||
    !nomineeForm.full_name.trim() ||
    !nomineeForm.gender ||
    !nomineeForm.dob ||
    !confirmed;

  return (
      <Box sx={{  p: 2, display: 'flex', flexDirection: 'column', gap: { xs:1, md:2 } }}>
        <Grid
          container
          spacing={2}
          sx={{ alignItems: 'stretch', mb: 2 }}
        >
          <Grid
            item
            xs={12}
            sm={6}
          >
            <BenefitCard
              item={insuranceData?.benefit_card}
              sx={{ height: '100%' }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
          >
            <Card
              sx={{
                boxShadow: 'none',
                p: 2,
                borderRadius: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-evenly',
              }}
            >
              {insuranceData?.selected_firm &&
                insuranceData.selected_firm.map((detail, i) => (
                  <React.Fragment key={i}>
                    <FirmDetails detail={detail} />
                  </React.Fragment>
                ))}
            </Card>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={2}
          sx={{ mb: 2 }}
        >
          {/* Beneficiary Details */}
          <Grid
            item
            xs={12}
            sm={6}
          >
            <Box sx={{ background: '#fff', gap: 2, borderRadius: 2, p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  borderRadius: 2,
                  background: '#fff',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant='h2'>Beneficiary Details</Typography>
                  <IconButton
                    sx={{ p: 0.5 }}
                    onClick={() => setShowDeleteModal(true)}
                  >
                    <IconReusable
                      name='Delete'
                      color='#995A00'
                      size={16}
                    />
                  </IconButton>
                </Box>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                >
                  {beneficiary_details?.existing_beneficiary?.details?.length > 0
                  ? [
                      { display_name: 'Name', display_value: beneficiary_details.existing_beneficiary.header_title },
                      ...beneficiary_details.existing_beneficiary.details,
                    ].map(({ display_name, display_value }) => (
                      <Box
                        key={display_name}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: '8px',
                        }}
                      >
                        <Typography variant='labelD' sx={{ color: '#666666' }}>
                          {display_name}
                        </Typography>
                        <Typography variant='p1' sx={{ textAlign: 'right' ,color: '#333333' }}>
                          {display_value || '—'}
                        </Typography>
                      </Box>
                    ))
                  : [
                      { label: 'Name',       value: beneficiary_details?.beneficiary?.full_name },
                      { label: 'Gender',     value: beneficiary_details?.beneficiary?.gender },
                      { label: 'D.O.B',      value: beneficiary_details?.beneficiary?.dob },
                      { label: 'Age',        value: beneficiary_details?.beneficiary?.dob ? getAge(beneficiary_details?.beneficiary?.dob) : '—' },
                      { label: 'Mobile No.', value: beneficiary_details?.beneficiary?.phone_number },
                      { label: 'PAN',        value: beneficiary_details?.beneficiary?.pan },
                    ].map(({ label, value }) => (
                      <Box
                        key={label}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: '8px',
                        }}
                      >
                        <Typography variant='labelD' sx={{ color: '#666666' }}>
                          {label}
                        </Typography>
                        <Typography variant='p1' sx={{ textAlign: 'right' , color:'#333333'}}>
                          {value}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Nominee Form */}
          <Grid
            item
            xs={12}
            sm={6}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography variant='h2'>Add Nominee Details</Typography>

              {/* Relation */}
              <CustomSelect
                id='nominee-relation-select'
                label='Relation*'
                value={nomineeForm.relation}
                onChange={(e) => handleFieldChange('relation', e.target.value)}
                error={!!errors.relation}
                helperText={errors.relation}
                displayEmpty
                fullWidth
              >
                <MenuItem
                  value=''
                  disabled
                >
                  Select Relation
                </MenuItem>
                {(insuranceData?.relations || []).map((opt) => (
                  <MenuItem
                    key={opt.value}
                    value={opt.value}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </CustomSelect>

              {/* Full Name */}
              <TextField
                label='Full Name*'
                value={nomineeForm.full_name}
                onChange={(e) => handleFieldChange('full_name', e.target.value)}
                onBlur={() => {
                  const name = nomineeForm.full_name.trim();
                  if (name && !/^[a-zA-Z\s]+$/.test(name)) {
                    setErrors((prev) => ({
                      ...prev,
                      full_name: 'Name can only contain English letters',
                    }));
                  }
                }}
                error={!!errors.full_name}
                helperText={errors.full_name}
                placeholder='Enter Full Name'
                fullWidth
              />

              {/* Gender */}
              <CustomSelect
                id='nominee-gender-select'
                label='Gender*'
                value={nomineeForm.gender}
                onChange={(e) => handleFieldChange('gender', e.target.value)}
                error={!!errors.gender}
                helperText={errors.gender}
                displayEmpty
                fullWidth
              >
                <MenuItem
                  value=''
                  disabled
                >
                  Select Gender
                </MenuItem>
                <MenuItem value='MALE'>Male</MenuItem>
                <MenuItem value='FEMALE'>Female</MenuItem>
              </CustomSelect>

              {/* DOB + Age row */}
              <Box
                sx={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}
              >
                <Box sx={{ flex: 1 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label='Date of Birth*'
                      value={nomineeForm.dob ? dayjs(nomineeForm.dob) : null}
                      onChange={(newValue) => {
                        const dateString = newValue
                          ? newValue.format('YYYY-MM-DD')
                          : '';
                        handleFieldChange('dob', dateString);
                      }}
                      error={!!errors.dob}
                      helperText={errors.dob}
                      disableFuture
                      format='DD/MM/YYYY'
                    />
                  </LocalizationProvider>
                </Box>
                {nomineeForm?.age && (
                  <Box
                    sx={{
                      width: '106px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <Typography
                      variant='labelD'
                      sx={{ color: '#666666' }}
                    >
                      Age
                    </Typography>
                    <Typography variant='p1' sx={{ fontSize: '16px !important'}}>
                      {nomineeForm.age ? `${nomineeForm.age}y` : '—'}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Consent checkbox */}
              {insuranceData?.confirmation_text?.show && (
                <Box>
                  <CustomCheckbox
                    label={insuranceData.confirmation_text.label}
                    checked={confirmed}
                    onChange={(e) => {
                      setConfirmed(e.target.checked);
                      if (e.target.checked && errors.confirmed) {
                        setErrors((prev) => {
                          const { confirmed: _, ...rest } = prev;
                          return rest;
                        });
                      }
                    }}
                  />
                  {errors.confirmed && (
                    <Typography
                      variant='caption'
                      color='error'
                      sx={{ mt: 0.5, display: 'block' }}
                    >
                      {errors.confirmed}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Submit */}
              <CustomButton
                variant='filled'
                label={submitting ? 'Submitting...' : 'Submit'}
                onClick={handleSubmit}
                disabled={isSubmitDisabled || submitting}
                fullWidth
              />
            </Box>
          </Grid>
        </Grid>

      <ExitModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => {
          dispatch(resetGapBeneficiary());
          navigate('/beneficiary/add-gap-beneficiary', {
            state: {
              insurance_type: insuranceData?.benefit_card?.name,
              selectedCompanyEntity: company_esi_id,
            },
          });
        }}
        title='Are you sure you want to delete?'
        subtitle='This action cannot be undone. All data would be lost?'
        confirmLabel='Yes'
        cancelLabel='No'
      />
      <ExitModal
        open={showExitModal}
        onClose={() => setShowExitModal(false)}
        onConfirm={() => navigate('/beneficiary')}
        title='Are you sure you want to exit?'
        subtitle='This action cannot be undone. All data would be lost?'
        confirmLabel='Yes'
        cancelLabel='No'
      />
    </Box>
  );
};

export default AddGapNominee;
